import { useEffect } from "react";
import Head from "next/head";
import dynamic from "next/dynamic";
// Holds the browser's install offer from the moment the page loads.
import "~/components/devices/installPrompt";

const HomeScreenNotificationSetup = dynamic(() =>
  import("~/components/integrations/HomeScreenNotificationSetup").then(
    (mod) => mod.HomeScreenNotificationSetup
  )
);

// Makes the dashboard installable (Add to Home Screen on iPhone, Install app
// on Android and desktop Chrome). iPhones deliver web push only to the
// installed app, so this has to be on the page members install from. The
// manifest is built per deployment: src/server/alerts/webAppManifest.ts.
// Browsers fetch a manifest without cookies unless the link says
// use-credentials. Staging and Preview sit behind Vercel Authentication, so
// without it the fetch is bounced to the Vercel sign-in and the app cannot
// be installed there.
// iPhone and iPad show this while the Home Screen app opens, instead of a
// blank white screen. iOS picks the image whose size matches the screen.
const LAUNCH_SCREENS: Array<[href: string, media: string]> = [
  ["/alerts/splash/launch-640x1136.png", "(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-750x1334.png", "(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-1242x2208.png", "(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-1125x2436.png", "(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-828x1792.png", "(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-1242x2688.png", "(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-1170x2532.png", "(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-1284x2778.png", "(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-1179x2556.png", "(device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-1290x2796.png", "(device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-1206x2622.png", "(device-width: 402px) and (device-height: 874px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-1320x2868.png", "(device-width: 440px) and (device-height: 956px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-1260x2736.png", "(device-width: 420px) and (device-height: 912px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"],
  ["/alerts/splash/launch-1488x2266.png", "(device-width: 744px) and (device-height: 1133px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-1536x2048.png", "(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-1620x2160.png", "(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-1640x2360.png", "(device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-1668x2388.png", "(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-1668x2420.png", "(device-width: 834px) and (device-height: 1210px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-2048x2732.png", "(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
  ["/alerts/splash/launch-2064x2752.png", "(device-width: 1032px) and (device-height: 1376px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"],
];

export function WebAppHead({
  showNotificationSetup = true,
}: {
  showNotificationSetup?: boolean;
}) {
  useClearAppBadge();
  return (
    <>
      <Head>
        <link
          rel="manifest"
          href="/api/alerts/manifest"
          crossOrigin="use-credentials"
        />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="TWT" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="theme-color" content="#faf9f5" />
        <link rel="apple-touch-icon" href="/alerts/icon-180.png" />
        {LAUNCH_SCREENS.map(([href, media]) => (
          <link key={href} rel="apple-touch-startup-image" href={href} media={media} />
        ))}
      </Head>
      {showNotificationSetup && <HomeScreenNotificationSetup />}
    </>
  );
}

// The app icon counts alerts that arrived while TWT was closed (public/sw.js).
// Seeing TWT clears the count.
function useClearAppBadge() {
  useEffect(() => {
    const clear = () => {
      if (document.visibilityState !== "visible") return;
      const nav = navigator as Navigator & { clearAppBadge?: () => Promise<void> };
      void nav.clearAppBadge?.().catch(() => undefined);
      void window.caches?.delete("twt-app-badge").catch(() => undefined);
    };
    clear();
    document.addEventListener("visibilitychange", clear);
    return () => document.removeEventListener("visibilitychange", clear);
  }, []);
}
